import { useCallback, useMemo } from "react";

import { format } from "date-fns";

import type { SMTAnalyticsSource } from "@/constants/analytics/Analytics";
import {
  AnalyticsFieldName,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { DateFormat } from "@/constants/DateFormat";
import { SMTTestID, TextTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import type { SMTEntryProps } from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import SMTEntry from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import { Accordion, AccordionTriggerChevron, Text, TextTag } from "@/ui/core";
import { Box, HStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const VerticalPadding = 18;

interface CustomAccordionTriggerProps extends ViewWithTestID {
  date: Date;
  entriesCount: number;
  isDisabled?: boolean;
  isOpen: boolean;
}

const CustomAccordionTrigger = ({
  date,
  entriesCount,
  isDisabled = false,
  isOpen,
  testID,
}: CustomAccordionTriggerProps) => {
  const calendarDay = format(date, DateFormat.fullMonthDay);
  const dayOfWeek = format(date, DateFormat.weekDay);
  return (
    <HStack
      alignItems="center"
      gap={10}
      justifyContent="space-between"
      width="100%"
    >
      <HStack alignItems="baseline" flexShrink={1} gap={10}>
        <Text
          testID={joinTestIDs(testID, TextTestID.Date)}
          variant="bodyMedium"
        >
          {calendarDay}
        </Text>

        <Text
          testID={joinTestIDs(testID, TextTestID.DayOfWeek)}
          variant="caption"
        >
          {dayOfWeek}
        </Text>
      </HStack>

      <HStack gap={10}>
        <TextTag
          text={entriesCount.toString()}
          variant={isDisabled ? "disabled" : "informational"}
        />
        {!isDisabled && (
          <AccordionTriggerChevron isOpen={isOpen} testID={testID} />
        )}
      </HStack>
    </HStack>
  );
};

interface SMTSingleDayEntriesAccordionProps extends ViewWithTestID {
  analyticsSource: ValueOf<typeof SMTAnalyticsSource>;
  dailyLogId: string;
  date: Date;
  entries: SMTEntryProps[];
  isOpen: boolean;
  onTriggerPress?: (isOpen: boolean) => void;
}

const SMTSingleDayEntriesAccordion = ({
  analyticsSource,
  dailyLogId,
  date,
  entries,
  isOpen,
  onTriggerPress,
  testID,
}: SMTSingleDayEntriesAccordionProps) => {
  const { track } = useAnalytics();
  const baseTestID = joinTestIDs(
    testID,
    SMTTestID.SMTSingleDayEntriesAccordion,
    dailyLogId,
  );
  const entriesCount = entries.length;
  const isDisabled = entriesCount === 0;
  const lastEntryId = entries.at(-1)?.smtEntryId;

  // a disabled (empty) day can never be open, regardless of any stale parent value
  const isAccordionOpen = isOpen && !isDisabled;

  const trackAccordion = useCallback(
    (isNextOpen: boolean) => {
      track(SMTEntryAnalyticsEvent.ViewSingleDayEntriesToggled, {
        "Accordion state": isNextOpen ? "Expanded" : "Collapsed",
        [AnalyticsFieldName.Component]: SMTSingleDayEntriesAccordion.name,
        [AnalyticsFieldName.Source]: analyticsSource,
        "Entries count": entriesCount,
        "Entries Date": format(date, DateFormat.yearMonthDay),
      });
    },
    [analyticsSource, date, entriesCount, track],
  );

  const handleTriggerPress = useCallback(
    (isNextOpen: boolean) => {
      if (isDisabled) return;
      trackAccordion(isNextOpen);
      onTriggerPress?.(isNextOpen);
    },
    [isDisabled, onTriggerPress, trackAccordion],
  );

  const customTrigger = useMemo(
    () => (
      <CustomAccordionTrigger
        date={date}
        entriesCount={entriesCount}
        isDisabled={isDisabled}
        isOpen={isAccordionOpen}
        testID={baseTestID}
      />
    ),
    [isAccordionOpen, date, entriesCount, isDisabled, baseTestID],
  );

  return (
    <Box testID={baseTestID}>
      <Box
        borderColor={ColorToken.NeutralLine}
        borderRadius={12}
        borderWidth={1}
      >
        <Accordion
          backgroundColor={ColorToken.NeutralBackgroundPrimary}
          borderWidth={0}
          customTrigger={customTrigger}
          isDisabled={isDisabled}
          isOpen={isAccordionOpen}
          onTriggerPress={handleTriggerPress}
          paddingVertical={VerticalPadding}
          testID={baseTestID}
          title=""
        >
          {entries.map((item) => {
            const {
              avatarUserId,
              avatarUserName,
              avatarUserType,
              details,
              photoContentType,
              photoUrl,
              smtEntryId,
              thumbnailUrl,
              time,
            } = item;
            const isLastItem = smtEntryId === lastEntryId;
            return (
              <Box key={smtEntryId}>
                <SMTEntry
                  avatarUserId={avatarUserId}
                  avatarUserName={avatarUserName}
                  avatarUserType={avatarUserType}
                  details={details}
                  photoContentType={photoContentType}
                  photoUrl={photoUrl}
                  smtEntryId={smtEntryId}
                  thumbnailUrl={thumbnailUrl}
                  time={time}
                />
                {!isLastItem && (
                  <Box
                    backgroundColor={ColorToken.NeutralLine}
                    height={1}
                    marginVertical={VerticalPadding}
                    testID={joinTestIDs(smtEntryId, "separator")}
                    width="100%"
                  />
                )}
              </Box>
            );
          })}
        </Accordion>
      </Box>
    </Box>
  );
};

export default SMTSingleDayEntriesAccordion;
export type { SMTSingleDayEntriesAccordionProps };
